import {
  ALL_ROWS_NAME,
  filterSchemaTree,
  getParentPath,
  includeAncestors,
  indexSchemaTree,
  SearchIndex,
  searchTree,
} from "portal/utils/configs";
import { capitalize } from "portal/utils/strings";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
} from "@mui/material";
import { classes, SMALL_TEXT_FIELD_DARK } from "portal/theme/theme";
import { debounce } from "portal/utils/timing";
import { GlobalHotKeys } from "react-hotkeys";
import { SchemaNode } from "protos/config/api/config_service";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { useLatest } from "common/hooks/useLatest";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withErrorBoundary } from "portal/components/ErrorBoundary";
import { without } from "portal/utils/arrays";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import CollapsedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandedIcon from "@mui/icons-material/ChevronRightOutlined";
import React, {
  FunctionComponent,
  memo,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";

// don't auto-expand search results when there are more matches than this;
// expanding a huge result set makes the (non-virtualized) tree unusably slow
const MAX_SEARCH_EXPANSIONS = 50;

interface NodeProps {
  node: SchemaNode;
  isListItem: boolean;
  path: string;
}

const TreeNode: FunctionComponent<NodeProps> = memo(function TreeNode({
  node,
  isListItem,
  path,
}) {
  const { t } = useTranslation();
  const childNode = (child: SchemaNode, isListItem: boolean): ReactNode => {
    const childPath = `${path}/${isListItem ? "*" : child.name}`;
    return (
      <TreeNode
        key={childPath}
        node={child}
        isListItem={isListItem}
        path={childPath}
      />
    );
  };

  let label;
  if (isListItem) {
    label = <em>{t("views.admin.config.bulk.listItems")}</em>;
  } else if (node.name === ALL_ROWS_NAME) {
    label = <em>{t("views.admin.config.bulk.allRows")}</em>;
  } else {
    label = <span className="font-mono">{node.name}</span>;
  }

  if (node.name === ALL_ROWS_NAME && node.listItem) {
    node = node.listItem;
  }

  return (
    <TreeItem
      slotProps={{
        content: {
          className:
            "py-0 [&[data-selected]]:font-bold [&[data-selected]]:bg-blue-600",
        },
      }}
      itemId={path}
      label={label}
    >
      {node.listItem && childNode(node.listItem, true)}
      {node.nodeChildren.map((child) => childNode(child, false))}
    </TreeItem>
  );
});

const TREE_SKELETON = (
  <>
    <Skeleton variant="text" className="w-10" />
    <Skeleton variant="text" className="w-16 ml-4" />
    <Skeleton variant="text" className="w-10 ml-4" />
    <Skeleton variant="text" className="w-12 ml-4" />
    <Skeleton variant="text" className="w-10" />
  </>
);

interface TreeProps {
  className?: string;
  schema?: SchemaNode;
  onSelect: (path: string | null) => void;
  selectedPath: string;
}

const _Tree: FunctionComponent<TreeProps> = ({
  className,
  schema,
  onSelect,
  selectedPath,
}) => {
  const { t } = useTranslation();

  const { state } = useLocation();

  const [expanded, _setExpanded] = useState<string[]>(() =>
    includeAncestors([selectedPath])
  );

  const setExpanded = useCallback(
    (paths: string[]): void => {
      // keep parent of selected node expanded
      _setExpanded([...paths, getParentPath(selectedPath)]);
    },
    [selectedPath]
  );
  const setExpandedRef = useLatest(setExpanded);

  const searchIndex: SearchIndex = useMemo(
    () => indexSchemaTree(schema),
    [schema]
  );
  const searchIndexRef = useLatest(searchIndex);

  const [savedExpanded, saveExpanded] = useState<string[]>([]);
  // searchInput is live text box; searchQuery is debounced
  const [searchInput, setSearchInput] = useState<string>(state?.search ?? "");
  const [searchQuery, setSearchQuery] = useState<string>(state?.search ?? "");
  // waiting on the debounce; isPending covers the tree re-render after it
  const [isWaiting, setWaiting] = useState<boolean>(false);
  const [isPending, startTransition] = useTransition();
  const isSearching = isWaiting || isPending;
  const debounceSearchState = useRef(0); // increment to cancel outstanding callback
  const finalizeSearchQuery = useMemo(
    () =>
      debounce((query, expectedState) => {
        if (debounceSearchState.current !== expectedState) {
          return;
        }
        setWaiting(false);
        const resultPaths = searchTree(searchIndexRef.current, query);

        // Mark the resulting tree update as non-urgent so keystrokes in the
        // search box stay responsive while the tree re-renders
        startTransition(() => {
          setSearchQuery(query);

          // Reset set of expanded nodes when search query changes. Access
          // search index and `setExpanded` through refs instead of memo
          // dependencies, since changes to the config and selection
          // should not cause this to re-trigger.
          const setExpanded = setExpandedRef.current;
          if (resultPaths.length > MAX_SEARCH_EXPANSIONS) {
            // too many matches to usefully auto-expand; show them collapsed
            setExpanded([]);
          } else {
            setExpanded(
              includeAncestors(
                // slice off the root node because the tree doesn't know
                // about it
                resultPaths.map((path) => path.split("/").slice(1).join("/"))
              )
            );
          }
        });
      }),
    [searchIndexRef, setExpandedRef, startTransition]
  );

  const handleSearchChange = (value: string): void => {
    if (!value) {
      clearSearch();
      return;
    }
    // save expanded nodes so we can restore later
    if (value && !searchInput) {
      saveExpanded(expanded);
    }
    // update value
    setSearchInput(value);
    setWaiting(true);
    finalizeSearchQuery(value, ++debounceSearchState.current);
  };
  const clearSearch = (): void => {
    ++debounceSearchState.current;
    setSearchInput("");
    setWaiting(false);
    startTransition(() => {
      setSearchQuery("");
      setExpanded([selectedPath, ...savedExpanded]);
    });
  };

  const filteredSchema: SchemaNode | undefined = useMemo(() => {
    if (!schema) {
      return schema;
    }
    const resultPaths = searchTree(searchIndex, searchQuery);
    return filterSchemaTree(schema, resultPaths);
  }, [schema, searchIndex, searchQuery]);

  // controlled tree
  const handleToggle = (
    _event: SyntheticEvent | null,
    nodeId: string,
    isExpanded: boolean
  ): void => {
    if (isExpanded) {
      setExpanded([...expanded, nodeId]);
    } else {
      setExpanded(without(expanded, nodeId));
    }
  };
  const handleSelect = (
    _event: SyntheticEvent | null,
    itemId: string | null
  ): void => {
    onSelect(itemId);
  };
  const handleItemClick = (_event: React.MouseEvent, itemId: string): void => {
    onSelect(itemId);
  };
  const inputRef = useRef<HTMLInputElement>();

  if (!schema) {
    return (
      <div className={classes(className, "flex")}>
        <div className="min-w-32">{TREE_SKELETON}</div>
        <div className="h-full flex-grow flex pl-8">
          <Skeleton variant="rectangular" className="h-8 w-40" />
          <Skeleton variant="rectangular" className="h-8 w-28 ml-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <GlobalHotKeys
        keyMap={{ FOCUS: ["/", "ctrl+f"] }}
        handlers={{
          FOCUS: (event) => {
            inputRef.current?.focus();
            event?.preventDefault();
          },
        }}
      />
      <TextField
        {...SMALL_TEXT_FIELD_DARK}
        inputRef={inputRef}
        value={searchInput}
        onChange={(event) => handleSearchChange(event.target.value)}
        className="w-full mr-5"
        label={t("utils.actions.search", {
          subject: capitalize(t("models.configs.config_other")),
        })}
        slotProps={{
          input: {
            ...SMALL_TEXT_FIELD_DARK.slotProps?.input,
            endAdornment: (
              <InputAdornment position="end">
                {isSearching && <CircularProgress size={16} />}
                <IconButton onClick={clearSearch} edge="end">
                  <ClearIcon
                    className={classes("text-white", {
                      hidden: !searchInput,
                    })}
                  />
                </IconButton>
              </InputAdornment>
            ),
          },

          htmlInput: { className: "font-mono" },
        }}
      />
      <div
        className={classes(
          "overflow-y-visible md:overflow-y-auto overflow-x-hidden basis-full md:basis-0 flex-grow-0 md:flex-grow",
          "transition-opacity",
          { "opacity-50": isSearching }
        )}
      >
        <SimpleTreeView
          className={classes(className)}
          // eslint-disable-next-line unicorn/no-null -- MUI X uses null for empty single-select
          selectedItems={selectedPath || null}
          expandedItems={expanded}
          onSelectedItemsChange={handleSelect}
          onItemClick={handleItemClick}
          onItemExpansionToggle={handleToggle}
          slots={{
            collapseIcon: CollapsedIcon,
            expandIcon: ExpandedIcon,
          }}
        >
          {filteredSchema?.nodeChildren.map((child) => (
            <TreeNode
              key={child.name}
              node={child}
              isListItem={false}
              path={child.name}
            />
          ))}
        </SimpleTreeView>
      </div>
    </div>
  );
};

export const SchemaTree = withErrorBoundary(
  { i18nKey: "views.fleet.robots.config.errors.failed" },
  _Tree
);
